/**
 * Shared helpers for the "Hire Experts" conversion flow.
 *
 * These are deliberately framework-free so they can be used by the provider,
 * the modal, the reusable form and the sticky mobile bar without importing
 * React. Nothing here touches the network or the existing submission pipeline —
 * it only holds the option lists, the role-from-page mapping and the
 * sessionStorage draft that survives closing/reopening the modal.
 */

export const HIRE_EXPERTS_PATH = '/request-consultants';
export const HIRE_DRAFT_KEY = 'cendien_hire_draft';
/** sessionStorage key for the just-submitted request reference (session only). */
export const HIRE_REF_KEY = 'cendien_hire_ref';

export interface HireRoleOption {
  value: string;
  label: string;
  icon: string;
}

/** Role / skill chips shown in Step 1. */
export const HIRE_ROLES: HireRoleOption[] = [
  { value: 'erp', label: 'ERP / Infor', icon: 'ri-stack-line' },
  { value: 'workday', label: 'Workday', icon: 'ri-user-settings-line' },
  { value: 'oracle', label: 'Oracle', icon: 'ri-database-2-line' },
  { value: 'microsoft', label: 'Microsoft / M365', icon: 'ri-windows-line' },
  { value: 'cloud', label: 'Cloud', icon: 'ri-cloud-line' },
  { value: 'cybersecurity', label: 'Cybersecurity', icon: 'ri-shield-keyhole-line' },
  { value: 'ai', label: 'AI / Data', icon: 'ri-brain-line' },
  { value: 'managed-it', label: 'Managed IT', icon: 'ri-settings-3-line' },
  { value: 'other', label: 'Other', icon: 'ri-more-line' },
];

export const HIRE_ENGAGEMENTS = [
  { value: 'contract', label: 'Contract' },
  { value: 'contract-to-hire', label: 'Contract-to-Hire' },
  { value: 'direct-hire', label: 'Direct Hire' },
  { value: 'managed-team', label: 'Managed Team' },
];

export const HIRE_HEADCOUNTS = ['1', '2–5', '6–10', '11–20', '20+'];

export const HIRE_TIMEFRAMES = [
  'Immediately',
  '1–2 weeks',
  '1 month',
  '2–3 months',
  'Flexible',
];

export interface HireDraft {
  roles: string[];
  engagement: string;
  headcount: string;
  timeframe: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

export const EMPTY_HIRE_DRAFT: HireDraft = {
  roles: [],
  engagement: '',
  headcount: '',
  timeframe: '',
  name: '',
  email: '',
  phone: '',
  company: '',
  message: '',
};

export interface HireOpenOptions {
  /** Pre-selected role value (overrides the page-derived role). */
  role?: string;
  /** Page the CTA was clicked from (defaults to the current pathname). */
  source?: string;
  /** Identifier of the CTA that opened the modal (for analytics/attribution). */
  cta?: string;
}

/** Maps the current page to a sensible default role so the modal pre-fills it. */
export function roleFromPath(pathname: string): string | null {
  const p = (pathname || '').toLowerCase();
  if (!p) return null;
  if (p.includes('/workday')) return 'workday';
  if (p.includes('/oracle')) return 'oracle';
  if (p.includes('/microsoft') || p.includes('/m365')) return 'microsoft';
  if (p.includes('/infor') || p.includes('/erp')) return 'erp';
  if (p.includes('/security') || p.includes('/cyber') || p.includes('/compliance')) return 'cybersecurity';
  if (p.includes('/cloud') || p.includes('/infrastructure')) return 'cloud';
  if (p.includes('/ai')) return 'ai';
  if (p.includes('/managed')) return 'managed-it';
  return null;
}

function isDraftShape(value: unknown): value is Partial<HireDraft> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Reads the persisted draft (SSR-safe, try/catch — never throws). */
export function loadHireDraft(): HireDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(HIRE_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isDraftShape(parsed)) return null;
    const str = (v: unknown): string => (typeof v === 'string' ? v : '');
    const roles = Array.isArray(parsed.roles)
      ? parsed.roles.filter((r): r is string => typeof r === 'string')
      : [];
    return {
      roles,
      engagement: str(parsed.engagement),
      headcount: str(parsed.headcount),
      timeframe: str(parsed.timeframe),
      name: str(parsed.name),
      email: str(parsed.email),
      phone: str(parsed.phone),
      company: str(parsed.company),
      message: str(parsed.message),
    };
  } catch {
    return null;
  }
}

/** Persists the draft for the session (try/catch — never throws). */
export function saveHireDraft(draft: HireDraft): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(HIRE_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* sessionStorage may be unavailable (private mode) — ignore */
  }
}

/** Clears the persisted draft (try/catch — never throws). */
export function clearHireDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(HIRE_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Records the reference of a request submitted in THIS browser session so the
 * /request-consultants page can show a dismissible "we received it" banner.
 * Only the opaque reference is stored — never any PII.
 */
export function saveHireRef(reference: string): void {
  if (typeof window === 'undefined' || !reference) return;
  try {
    window.sessionStorage.setItem(HIRE_REF_KEY, reference);
    window.dispatchEvent(new CustomEvent('cendien:hire-submitted', { detail: reference }));
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

/** Reads the reference of a request submitted in this session (or null). */
export function loadHireRef(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(HIRE_REF_KEY);
  } catch {
    return null;
  }
}

/** Dismisses the success banner for this session. */
export function clearHireRef(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(HIRE_REF_KEY);
  } catch {
    /* ignore */
  }
}

/** Turns free text into a short slug used as a fallback CTA identifier. */
export function slugifyCta(text: string): string {
  return (text || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}